import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ExternalLinkIcon, LightbulbIcon, MinusIcon, MousePointerClickIcon, StethoscopeIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
import { ScaledEmbed } from '../ScaledEmbed';
import { jascaydAudience, jascaydConcept, jascaydMarkets } from '../../data/jascaydOffer';
export function SlideJascaydFocus({}: SlideProps) {
  return <InteriorSlide data-id="element-255" data-mp="3u4zldpomic4w">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="flex-shrink-0" data-id="element-256" data-mp="3u4gdz2wln654">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-257" data-mp="7xj9dd86bw64">
          Proposed Jascayd campaign
        </p>
        <h2 className="max-w-5xl text-[clamp(1.9rem,3.6vw,3.3rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-258" data-mp="24rw53ihzwl6u">
          Two specialties. One region.{' '}
          <span className="text-[var(--brand)]" data-id="element-259" data-mp="5pu0vfucs5cc">One focused Snak campaign.</span>
        </h2>
      </motion.header>

      <div className="mt-[clamp(1rem,2vw,1.75rem)] grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.75rem,1.5vw,1.35rem)] lg:grid-cols-[minmax(0,4fr)_minmax(0,6fr)]" data-id="element-260" data-mp="x10t59txl6l">
        {/* Left: who, where, concept lead-in */}
        <div className="flex min-h-0 flex-col gap-[clamp(0.6rem,1.2vw,1.1rem)]" data-id="element-261" data-mp="3o0ys81wz23vv">
          <motion.section initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15,
          duration: 0.45
        }} className="flex-shrink-0 rounded-2xl bg-[var(--surface)] p-[clamp(0.85rem,1.5vw,1.35rem)]" data-id="element-262" data-mp="on51p4vmv6tw">
            <div className="grid grid-cols-[auto_1fr] items-start gap-x-[clamp(0.75rem,1.4vw,1.25rem)] gap-y-[clamp(0.6rem,1.1vw,0.9rem)]" data-id="element-263" data-mp="andjst1zx6am">
              <p className="pt-1.5 text-[clamp(0.6rem,0.85vw,0.75rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-264" data-mp="2vedyiut0xfp5">
                Who
              </p>
              <ul className="flex flex-wrap gap-[clamp(0.35rem,0.7vw,0.55rem)]" data-id="element-265" data-mp="2wwcm9gbasbsl">
                {jascaydAudience.map(a => <li key={a.label} className="inline-flex items-center gap-1.5 rounded-full bg-white px-[clamp(0.65rem,1vw,0.9rem)] py-[clamp(0.3rem,0.55vw,0.45rem)] text-[clamp(0.78rem,1.05vw,0.98rem)] font-bold text-[var(--fg)] ring-1 ring-[var(--brand)]/20" data-id="element-266" data-mp="2nuv9o7x3ms8n">
                    <StethoscopeIcon className="h-3.5 w-3.5 text-[var(--brand)]" aria-hidden="true" data-id="element-267" data-mp="2iwa927dxq6jo" />
                    {a.label}
                  </li>)}
              </ul>

              <p className="pt-1.5 text-[clamp(0.6rem,0.85vw,0.75rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-268" data-mp="295pagjy9h20r">
                Where
              </p>
              <ul className="flex flex-wrap gap-[clamp(0.35rem,0.7vw,0.55rem)]" data-id="element-269" data-mp="1v8afkzp8o21e">
                {jascaydMarkets.map(m => <li key={m.name} className={`inline-flex items-center gap-1 rounded-full px-[clamp(0.6rem,0.9vw,0.8rem)] py-[clamp(0.25rem,0.5vw,0.4rem)] text-[clamp(0.72rem,0.95vw,0.9rem)] font-semibold ${m.inScope ? 'bg-white text-[var(--fg)] ring-1 ring-[var(--brand)]/20' : 'text-[var(--muted)] line-through ring-1 ring-[var(--fg)]/10'}`} data-id="element-270" data-mp="1gj8wkg9kz6fu">
                    {m.inScope ? <CheckIcon className="h-3.5 w-3.5 text-[var(--brand)]" aria-hidden="true" data-id="element-271" data-mp="1uigxbdle6mm5" /> : <MinusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-272" data-mp="297d4l8fmjvlp" />}
                    {m.name}
                  </li>)}
              </ul>
            </div>
          </motion.section>

          <motion.section initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25,
          duration: 0.45
        }} className="flex min-h-0 flex-1 flex-col rounded-2xl bg-[var(--fg)] p-[clamp(1rem,1.8vw,1.6rem)] text-white" data-id="element-273" data-mp="2da1bhaks2sg1">
            <p className="inline-flex items-center gap-1.5 text-[clamp(0.6rem,0.85vw,0.75rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand-lighter)]" data-id="element-274" data-mp="lanerpdkn3pj">
              <LightbulbIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-275" data-mp="pb7cov5yybcq" />
              Campaign concept · a starting idea
            </p>
            <h3 className="mt-[clamp(0.5rem,1vw,0.8rem)] text-[clamp(1.1rem,1.7vw,1.6rem)] font-extrabold leading-tight tracking-[-0.02em]" data-id="element-276" data-mp="3tpx92bb7z4mr">
              {jascaydConcept.name}
            </h3>
            <p className="mt-[clamp(0.4rem,0.8vw,0.65rem)] text-[clamp(0.8rem,1.05vw,1rem)] leading-snug text-white/75" data-id="element-277" data-mp="6l93gbqfqc7i">
              {jascaydConcept.lead}
            </p>
            <ol className="mt-[clamp(0.6rem,1.1vw,0.9rem)] flex flex-wrap gap-1.5" data-id="element-278" data-mp="1d3nr8vqhzl7a">
              {jascaydConcept.flow.map((step, i) => <li key={step} className={`rounded-full px-2.5 py-1 text-[clamp(0.65rem,0.85vw,0.8rem)] font-semibold ${step === 'Your estimate' ? 'bg-[var(--brand-light)] text-white' : 'bg-white/10 text-white/85'}`} data-id="element-279" data-mp="1s54ga8w4il6f" data-mpi={i}>
                  {i + 1}. {step}
                </li>)}
            </ol>
            <div className="mt-auto flex flex-wrap items-center justify-between gap-2 pt-[clamp(0.6rem,1.1vw,0.9rem)]" data-id="element-280" data-mp="19fekbigmjtcx">
              <p className="text-[clamp(0.68rem,0.9vw,0.82rem)] text-white/55" data-id="element-281" data-mp="14hd20zr0vy2i">
                Final topic and message agreed with the brand team.
              </p>
              <a href={jascaydConcept.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[clamp(0.7rem,0.9vw,0.85rem)] font-bold text-[var(--fg)] transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-lighter)]" data-id="element-282" data-mp="vfwq2nkk7nw6">
                Open full screen
                <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-283" data-mp="e59j4s3nwzgm" />
              </a>
            </div>
          </motion.section>
        </div>

        {/* Right: live concept embed */}
        <motion.figure initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.35,
        duration: 0.5
      }} className="flex min-h-[16rem] min-w-0" data-id="element-284" data-mp="15wfg4zo36h1o">
          <ScaledEmbed src={jascaydConcept.url} title={`${jascaydConcept.name} — interactive Snak concept`} chromeHeight={38} frameClassName="rounded-2xl bg-[#111614] shadow-[0_18px_40px_rgba(0,84,61,0.28)] ring-2 ring-[var(--brand-light)]" chrome={<div className="flex h-full items-center gap-2 border-b border-white/5 px-3" data-id="element-286" data-mp="3mlkwa4okir64">
                <span className="h-2.5 w-2.5 rounded-full bg-white/20" data-id="element-287" data-mp="3e2regk19mlrr" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/20" data-id="element-288" data-mp="3a2h4yxe9ld85" />
                <span className="h-2.5 w-2.5 rounded-full bg-white/20" data-id="element-289" data-mp="3a4juzhff36cv" />
                <figcaption className="ml-2 min-w-0 flex-1 truncate text-[clamp(0.62rem,0.8vw,0.75rem)] font-semibold text-white/50" data-id="element-290" data-mp="3o1gmll3d0rut">
                  fibroneer-ipf.snakker.io
                </figcaption>
                <motion.span animate={{
            scale: [1, 1.05, 1]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-[var(--brand-light)] px-2.5 py-1 text-[clamp(0.62rem,0.82vw,0.78rem)] font-bold text-white" data-id="element-291" data-mp="2vdkrgktfv805">
                  <span className="relative flex h-2 w-2" data-id="element-292" data-mp="2clrygqeddmsf">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" data-id="element-293" data-mp="27nqdfzxdvdeb" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-white" data-id="element-294" data-mp="1m4x8q71yjggz" />
                  </span>
                  <MousePointerClickIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-295" data-mp="1r4joan59d8sy" />
                  Interactive — click through it live
                </motion.span>
              </div>} data-id="element-285" data-mp="wwaihh8p2x0p" />
        </motion.figure>
      </div>
    </InteriorSlide>;
}